import { Component, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { UserService } from '../../../../services/user';
import { BrandService } from '../../../../services/brandservice';

const IMAGE_BASE_URL = 'https://backendproduction.blueboxpet.com/storage/app/public/';
type BrandAddMode = 'new' | 'existing';

@Component({
  selector: 'app-createbrand',
  templateUrl: './createbrand.component.html',
  styleUrl: './createbrand.component.scss'
})
export class CreatebrandComponent {

  contentReady = false;
  data!: FormGroup;
  isUpdatePage = false;
  itemId!: string;

  isMobile = false;
  isAr = false;
  isAdmin = false;
  brandAddMode: BrandAddMode = 'existing';
  brandDropdownOpen = true;
  systemBrands: any[] = [];
  availableSystemBrands: any[] = [];
  selectedSystemBrand: any = null;
  selectedSystemBrandId = '';
  selectedSystemBrands: any[] = [];
  selectedSystemBrandIds: string[] = [];
  systemBrandsSearch = '';
  loadingSystemBrands = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private _brand: BrandService,
    private _userService: UserService,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') === 'ar';

    this.initForm();
    this.configureBrandFieldsForMode();
    this.handleAccountType();
    this.handleEditMode();
  }

  // ✅ Reactive Form فقط
  initForm() {
    this.data = this.fb.group({
      brandname_ar: ['', Validators.required],
      brandname_en: ['', Validators.required],
      brand_description_ar: [''],
      brand_description_en: [''],
      warehouse_id: [''],
      is_local_brand: [false],
      existing_brand_id: [''],
    });
  }

  handleAccountType() {
    this._userService.getAccountType().subscribe(res => {
      const type = res['user_type'];
      this.isAdmin = type === '1' || type === '10';
    });
  }

  handleEditMode() {
    this.route.queryParams.subscribe(params => {
      if (!params['id']) {
        this.openExistingBrandPickerByDefault();
        return;
      }

      this.isUpdatePage = true;
      this.brandAddMode = 'new';
      this.brandDropdownOpen = false;
      this.itemId = params['id'];

      this.data.patchValue({
        brandname_ar: params['brandname_ar'],
        brandname_en: params['brandname_en'],
        brand_description_ar: params['brand_description_ar'],
        brand_description_en: params['brand_description_en'],
        is_local_brand: this.parseBoolean(params['is_local_brand'])
      });
    });
  }

  buildFormData(existingBrand?: any): FormData {
    const formData = new FormData();

    const formValue = (existingBrand ? {
      ...this.data.getRawValue(),
      existing_brand_id: existingBrand.id || '',
      brandname_ar: existingBrand.brandname_ar || '',
      brandname_en: existingBrand.brandname_en || '',
      brand_description_ar: existingBrand.brand_description_ar || '',
      brand_description_en: existingBrand.brand_description_en || '',
      is_local_brand: this.parseBoolean(existingBrand.is_local_brand)
    } : this.data.getRawValue()) as {
      [key: string]: string | boolean | null;
    };

    Object.keys(formValue).forEach(key => {
      let value = formValue[key];

      if (value === null || value === undefined) {
        formData.append(key, '');
      } else if (typeof value === 'boolean') {
        formData.append(key, value ? '1' : '0');
      } else {
        formData.append(key, value.toString());
      }
    });

    // warehouse_id من localStorage
    formData.set('warehouse_id', localStorage.getItem('posId') || '');
    if (this.brandAddMode === 'existing') {
      const existingBrandId = String(existingBrand?.id ?? this.selectedSystemBrandId ?? '');
      formData.set('existing_brand_id', existingBrandId);
      formData.set('source_brand_id', existingBrandId);
    }

    return formData;
  }
  parseBoolean(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  onSubmit() {
    if (this.brandAddMode === 'existing') {
      this.data.patchValue({ existing_brand_id: this.selectedSystemBrandIds.join(',') }, { emitEvent: false });
      this.data.get('existing_brand_id')?.updateValueAndValidity();
    }

    if (this.data.invalid) return;

    this.contentReady = true;

    if (this.brandAddMode === 'existing' && this.selectedSystemBrands.length > 0 && !this.isUpdatePage) {
      this.createExistingBrands();
      return;
    }

    const payload = this.buildFormData();

    this.isUpdatePage
      ? this.updateBrand(payload)
      : this.createBrand(payload);
  }

  createBrand(payload: FormData) {
    this._brand.create(payload).subscribe({
      next: () => this.handleSuccess('brand created', 'تم اضافة البرند'),
      error: (error) => this.handleError('brand not created', 'فشل حفظ البرند', error)
    });
  }

  createExistingBrands() {
    const warehouseId = localStorage.getItem('posId') || '';
    const brandIds = this.selectedSystemBrandIds
      .map(id => Number(id))
      .filter(id => Number.isFinite(id));

    this._brand.addExistingBrandsToWarehouse(warehouseId, brandIds).subscribe({
      next: () => this.handleSuccess('brand created', 'تم اضافة البرند'),
      error: (error) => this.handleError('brand not created', 'فشل حفظ البرند', error)
    });
  }

  updateBrand(payload: FormData) {
    this._brand.update(this.itemId, payload).subscribe({
      next: () => this.handleSuccess('brand changed', 'تم تعديل البرند'),
      error: (error) => this.handleError('brand not changed', 'لم يتم تعديل البرند', error)
    });
  }

  handleSuccess(en: string, ar: string) {
    Swal.fire({
      icon: 'success',
      title: this.isAr ? ar : en,
      showConfirmButton: false,
      timer: 1500
    });

    this.contentReady = false;
    this.router.navigateByUrl(this.isMobile ? '/brands.moblie' : '/brands');
  }

  handleError(en: string, ar: string, error?: any) {
    const backendMessage = error?.error?.message || error?.message || '';

    Swal.fire({
      icon: 'error',
      title: this.isAr ? ar : en,
      text: backendMessage
    });
    this.contentReady = false;
  }

  setBrandAddMode(mode: BrandAddMode): void {
    if (this.isUpdatePage) return;

    this.brandAddMode = mode;
    this.clearSelectedSystemBrand(mode === 'existing');
    this.configureBrandFieldsForMode();

    if (mode === 'existing') {
      this.openExistingBrandPickerByDefault();
    } else {
      this.brandDropdownOpen = false;
    }
  }

  toggleBrandDropdown(): void {
    this.brandDropdownOpen = !this.brandDropdownOpen;

    if (this.brandDropdownOpen && this.availableSystemBrands.length === 0 && !this.loadingSystemBrands) {
      this.loadSystemBrandsForWarehouse();
    }
  }

  closeBrandDropdown(): void {
    this.brandDropdownOpen = false;
  }

  get displayedSystemBrands(): any[] {
    const query = this.cleanComparableValue(this.systemBrandsSearch);

    if (!query) {
      return this.availableSystemBrands;
    }

    return this.availableSystemBrands.filter(brand =>
      this.getBrandSearchText(brand).includes(query)
    );
  }

  toggleSystemBrand(brand: any): void {
    const brandId = String(brand?.id ?? '');
    if (!brandId) return;

    if (this.isSystemBrandSelected(brand)) {
      this.selectedSystemBrands = this.selectedSystemBrands.filter(
        item => String(item.id) !== brandId
      );
    } else {
      this.selectedSystemBrands = [...this.selectedSystemBrands, brand];
    }

    this.syncSelectedSystemBrands();
  }

  toggleAllSystemBrands(): void {
    if (this.areAllSystemBrandsSelected()) {
      const visibleBrandIds = new Set(this.displayedSystemBrands.map(brand => String(brand.id)));
      this.selectedSystemBrands = this.selectedSystemBrands.filter(
        brand => !visibleBrandIds.has(String(brand.id))
      );
    } else {
      const selectedBrandsMap = new Map<string, any>();
      [...this.selectedSystemBrands, ...this.displayedSystemBrands].forEach(brand => {
        selectedBrandsMap.set(String(brand.id), brand);
      });
      this.selectedSystemBrands = Array.from(selectedBrandsMap.values());
    }

    this.syncSelectedSystemBrands();
  }

  isSystemBrandSelected(brand: any): boolean {
    return this.selectedSystemBrandIds.includes(String(brand?.id ?? ''));
  }

  areAllSystemBrandsSelected(): boolean {
    return this.displayedSystemBrands.length > 0 &&
      this.displayedSystemBrands.every(brand => this.isSystemBrandSelected(brand));
  }

  getSelectedBrandsCount(): number {
    return this.selectedSystemBrands.length;
  }

  getBrandDisplayName(brand: any): string {
    if (!brand) return '';
    return this.isAr
      ? (brand.brandname_ar || brand.brandname_en || '')
      : (brand.brandname_en || brand.brandname_ar || '');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (!target.closest('.bb-brand-existing-field')) {
      this.closeBrandDropdown();
    }
  }

  getBrandImage(brand: any): string {
    const image = brand?.brand_logo || brand?.image || brand?.image_url;
    if (!image) return '/assets/no-image.png';
    if (String(image).startsWith('http')) return image;
    return IMAGE_BASE_URL + image;
  }

  onBrandImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.src = '/assets/no-image.png';
  }

  private configureBrandFieldsForMode(): void {
    const brandFields = ['brandname_ar', 'brandname_en', 'brand_description_ar', 'brand_description_en', 'is_local_brand'];
    const shouldDisableBrandFields = this.brandAddMode === 'existing';

    brandFields.forEach(field => {
      const control = this.data.get(field);
      if (!control) return;

      if (shouldDisableBrandFields) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }

      if (field === 'brandname_ar' || field === 'brandname_en') {
        control.setValidators(shouldDisableBrandFields ? [] : [Validators.required]);
      }

      control.updateValueAndValidity({ emitEvent: false });
    });

    const existingBrandControl = this.data.get('existing_brand_id');
    if (existingBrandControl) {
      existingBrandControl.setValidators(this.brandAddMode === 'existing' ? [Validators.required] : []);
      existingBrandControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private loadSystemBrandsForWarehouse(): void {
    if (this.isUpdatePage) {
      this.availableSystemBrands = [];
      return;
    }

    const warehouseId = localStorage.getItem('posId') || '';
    this.loadingSystemBrands = true;

    this._brand.getAvailableSystemBrands(warehouseId).subscribe({
      next: (response) => {
        this.systemBrands = this.extractBrands(response);
        this.availableSystemBrands = this.systemBrands;
        this.selectedSystemBrands = this.selectedSystemBrands.filter(selectedBrand =>
          this.availableSystemBrands.some(brand => String(brand.id) === String(selectedBrand.id))
        );
        this.syncSelectedSystemBrands();
        this.loadingSystemBrands = false;
      },
      error: (error) => {
        console.error(error);
        this.availableSystemBrands = [];
        this.loadingSystemBrands = false;
      }
    });
  }

  private openExistingBrandPickerByDefault(): void {
    if (this.isUpdatePage) return;

    this.brandAddMode = 'existing';
    this.brandDropdownOpen = true;
    this.configureBrandFieldsForMode();

    if (this.availableSystemBrands.length === 0 && !this.loadingSystemBrands) {
      this.loadSystemBrandsForWarehouse();
    }

    setTimeout(() => {
      if (!this.isUpdatePage && this.brandAddMode === 'existing') {
        this.brandDropdownOpen = true;
      }
    });
  }

  private extractBrands(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.brands?.data)) return response.brands.data;
    if (Array.isArray(response?.brands)) return response.brands;
    if (Array.isArray(response?.items)) return response.items;
    return [];
  }

  private getBrandSearchText(brand: any): string {
    return [
      brand?.brandname_ar,
      brand?.brandname_en,
      brand?.brand_description_ar,
      brand?.brand_description_en
    ].map(value => this.cleanComparableValue(value)).join(' ');
  }

  private applySystemBrand(brand: any): void {
    this.data.patchValue({
      existing_brand_id: brand?.id || '',
      brandname_ar: brand?.brandname_ar || '',
      brandname_en: brand?.brandname_en || '',
      brand_description_ar: brand?.brand_description_ar || '',
      brand_description_en: brand?.brand_description_en || '',
      is_local_brand: this.parseBoolean(brand?.is_local_brand)
    }, { emitEvent: false });
  }

  private syncSelectedSystemBrands(): void {
    this.selectedSystemBrandIds = this.selectedSystemBrands.map(brand => String(brand.id));
    this.selectedSystemBrandId = this.selectedSystemBrandIds[0] || '';
    this.selectedSystemBrand = this.selectedSystemBrands[0] || null;

    this.data?.patchValue({
      existing_brand_id: this.selectedSystemBrandIds.join(',')
    }, { emitEvent: false });

    if (this.selectedSystemBrand) {
      this.applySystemBrand(this.selectedSystemBrand);
    } else if (this.brandAddMode === 'existing') {
      this.data?.patchValue({
        brandname_ar: '',
        brandname_en: '',
        brand_description_ar: '',
        brand_description_en: '',
        is_local_brand: false
      }, { emitEvent: false });
    }

    this.data?.get('existing_brand_id')?.markAsTouched();
    this.data?.get('existing_brand_id')?.updateValueAndValidity({ emitEvent: false });
  }

  private clearSelectedSystemBrand(clearBrandData = false): void {
    this.selectedSystemBrand = null;
    this.selectedSystemBrandId = '';
    this.selectedSystemBrands = [];
    this.selectedSystemBrandIds = [];
    this.systemBrandsSearch = '';
    this.data?.patchValue({ existing_brand_id: '' }, { emitEvent: false });

    if (clearBrandData) {
      this.data?.patchValue({
        brandname_ar: '',
        brandname_en: '',
        brand_description_ar: '',
        brand_description_en: '',
        is_local_brand: false
      }, { emitEvent: false });
    }
  }

  private cleanComparableValue(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }
}
